// src/components/Navbar.js
import React from "react";
import { NavLink, useLocation } from "react-router-dom";
import { FiMenu, FiX, FiArrowRight } from "react-icons/fi";
import "./Navbar.css";
import logoImg from "../assets/minds_logo.png";
import { Button } from "./UI";

const SECTIONS = [
  { id: "top", label: "Home" },
  { id: "mission", label: "Our Mission" },
  { id: "terms", label: "Term Dates" },
  { id: "register", label: "Register" },
  { id: "contact", label: "Contact" },
  { id: "careers", label: "Careers" },
];

const REGISTER_FORM_URL =
  "https://docs.google.com/forms/d/e/1FAIpQLSekY0f-1MVUQxv7zL_LX-unCKOvZoH3YoJtUavxOEAG6hLRrQ/viewform?pli=1";

function scrollToId(id) {
  const el = document.getElementById(id);
  if (!el) return;
  el.scrollIntoView({ behavior: "smooth", block: "start" });
}

export default function Navbar() {
  const [scrolled, setScrolled] = React.useState(false);
  const [open, setOpen] = React.useState(false);
  const [active, setActive] = React.useState("top");
  const location = useLocation();

  React.useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  React.useEffect(() => {
    if (location.pathname !== "/") return;

    const ids = SECTIONS.map((s) => s.id);
    const els = ids.map((id) => document.getElementById(id)).filter(Boolean);
    if (!els.length) return;

    // Sticky nav is ~92px in your CSS scroll-margin-top
    // rootMargin makes “active” switch happen when section crosses under the nav.
    const io = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => (b.intersectionRatio || 0) - (a.intersectionRatio || 0))[0];

        if (visible?.target?.id) setActive(visible.target.id);

        // If nothing intersecting and we are near top, force Home active
        if (!visible && window.scrollY < 140) setActive("top");
      },
      {
        root: null,
        // top margin is negative so we consider sections "entered" after they pass the sticky navbar
        rootMargin: "-100px 0px -65% 0px",
        threshold: [0.08, 0.15, 0.25],
      }
    );

    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, [location.pathname]);

  const onNav = (id) => {
    setOpen(false);

    // Only scroll on Home route — if you ever add other routes later, this prevents weird behavior.
    if (location.pathname !== "/") return;

    if (id === "top") window.scrollTo({ top: 0, behavior: "smooth" });
    else scrollToId(id);
  };

  return (
    <header className={`navWrap ${scrolled ? "scrolled" : ""}`}>
      <div className="navInner">
        <NavLink
          to="/"
          className="brand"
          aria-label="Minds home"
          onClick={(e) => {
            e.preventDefault();
            onNav("top");
          }}
        >
          <img className="brandLogo" src={logoImg} alt="Minds" />
          <div className="brandStack">
            <div className="brandText">Minds</div>
            <div className="brandSub">Educational Support</div>
          </div>
        </NavLink>

        <nav className="navLinks" aria-label="Primary">
          {SECTIONS.slice(1).map((s) => (
            <button
              key={s.id}
              type="button"
              className={`navLinkBtn ${active === s.id ? "active" : ""}`}
              onClick={() => onNav(s.id)}
            >
              {s.label}
            </button>
          ))}
        </nav>

        <div className="navRight">
          <Button
            as="a"
            href={REGISTER_FORM_URL}
            target="_blank"
            rel="noreferrer"
            variant="primary"
            className="registerBtn"
            iconRight={<FiArrowRight />}
          >
            Register now
          </Button>

          <button
            className="navMenuBtn"
            type="button"
            onClick={() => setOpen(true)}
            aria-label="Open menu"
          >
            <FiMenu />
          </button>
        </div>
      </div>

      {open ? (
        <div className="navDrawerBackdrop" onClick={() => setOpen(false)} role="presentation">
          <div className="navDrawer" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
            <div className="drawerTop">
              <div className="drawerTitle">Menu</div>
              <button className="drawerClose" type="button" onClick={() => setOpen(false)} aria-label="Close menu">
                <FiX />
              </button>
            </div>

            <div className="drawerLinks">
              {SECTIONS.map((s) => (
                <button
                  key={s.id}
                  type="button"
                  className={`drawerLinkBtn ${active === s.id ? "active" : ""}`}
                  onClick={() => onNav(s.id)}
                >
                  {s.label}
                </button>
              ))}
            </div>

            <div className="drawerBottom">
              <Button
                as="button"
                type="button"
                onClick={() => onNav("register")}
                variant="primary"
                iconRight={<FiArrowRight />}
              >
                Register now
              </Button>
              <div className="drawerTiny">Where Knowledge Meets Nurture</div>
            </div>
          </div>
        </div>
      ) : null}
    </header>
  );
}